import { CANONICAL_ORIGIN, siteAssetUrl } from "@/content/types";
import { shareTagsFor } from "./shareTags";

const GOOGLE_SITE_VERIFICATION = "Ix9BFb8eMLcO5rEEqPQ6Z3Y6Pc1WBF86lOlqKDHlpVM";

/**
 * The blog's share card, which the academy's pages also fall back to: the
 * published academy pages carry no card of their own.
 */
/** The one share card the whole site publishes; no page carries its own. */
const DEFAULT_SHARE_IMAGE = siteAssetUrl(
  "/marketing/webflow/67768f30fcfcfc72af150429_open-graph.webp"
);

/**
 * Crawlers and social scrapers ignore a relative `og:image`, and the code pages
 * author theirs as a site-root path into `public/marketing/`. Resolving against
 * the canonical origin lifts those and leaves a CMS card's CDN URL alone.
 */
function shareImageUrl(shareImage: string): string {
  return new URL(shareImage, CANONICAL_ORIGIN).href;
}

type MarketingHeadProps = {
  title: string;
  description: string;
  /** The page's own address, which the language alternates always name. */
  canonical: string;
  /**
   * The address an editor set in the page's SEO tab for the canonical tag
   * alone, where the page names another as the original.
   */
  canonicalOverride?: string;
  /** The headline a shared link shows, where the page sets one apart from its title. */
  socialTitle?: string;
  shareImage?: string;
};

/** The description tag, and the share tags that carry it, where the page prints them. */
function DescriptionTags({
  description,
  share,
  empty,
}: {
  description: string;
  share: boolean;
  empty: boolean;
}) {
  if (!description && !empty) return null;
  return (
    <>
      <meta name="description" content={description} />
      {share ? <ShareDescription description={description} /> : null}
    </>
  );
}

/** `og:description` and `twitter:description`, with the type and card that go with them. */
function ShareDescription({ description }: { description: string }) {
  return (
    <>
      <meta property="og:description" content={description} />
      <meta name="twitter:description" content={description} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
    </>
  );
}

/** `og:title` and `twitter:title`. */
function ShareTitle({ title }: { title: string }) {
  return (
    <>
      <meta property="og:title" content={title} />
      <meta name="twitter:title" content={title} />
    </>
  );
}

/** `og:image` and `twitter:image`. */
function ShareImage({ href }: { href: string }) {
  return (
    <>
      <meta property="og:image" content={href} />
      <meta name="twitter:image" content={href} />
    </>
  );
}

/**
 * Every head tag a marketing page publishes. Which share tags it prints is the
 * published page's own choice (`shareTags.ts`): most print all of them, a
 * template or a page leaves some out, and a page with no description prints
 * none of the tags that carry it, except on the templates that print them
 * empty.
 *
 * Plain JSX rather than a route `head` option: React 19 lifts `title`, `meta`
 * and `link` out of the render tree into `document.head`, which keeps the tags
 * next to the data that fills them. Structured data is the exception, a
 * `script` is not hoisted, so `$slug.tsx` emits that one from the route.
 */
export default function MarketingHead({
  title,
  description,
  canonical,
  canonicalOverride,
  socialTitle,
  shareImage = DEFAULT_SHARE_IMAGE,
}: MarketingHeadProps) {
  const share = shareTagsFor(new URL(canonical).pathname);
  return (
    <>
      <title>{title}</title>
      {share.title ? <ShareTitle title={socialTitle || title} /> : null}
      {share.image ? <ShareImage href={shareImageUrl(shareImage)} /> : null}
      <DescriptionTags
        description={description}
        share={share.description}
        empty={share.emptyDescription}
      />
      <meta
        name="google-site-verification"
        content={GOOGLE_SITE_VERIFICATION}
      />
      <link rel="shortcut icon" type="image/x-icon" href="/favicon.png" />
      <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
      <link rel="canonical" href={canonicalOverride || canonical} />
      <link rel="alternate" hrefLang="en-us" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />
    </>
  );
}
